import { useEffect } from 'react';
import { createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { reactRouterV6BrowserTracingIntegration } from '@sentry/react';

function buildTracePropagationTargets(): (string | RegExp)[] {
    const targets: (string | RegExp)[] = ['localhost', /^https:\/\/bd-staging\.caeruscorp\.tech/, /^https:\/\/bd-prod\.caeruscorp\.tech/];

    const apiUrl = process.env.REACT_APP_API_URL?.trim();
    if (apiUrl) {
        try {
            const { origin } = new URL(apiUrl);
            if (!targets.includes(origin)) {
                targets.push(origin);
            }
        } catch {
            // ignore invalid REACT_APP_API_URL
        }
    }

    return targets;
}

function resolveTracesSampleRate(): number {
    const configured = process.env.REACT_APP_SENTRY_TRACES_SAMPLE_RATE;
    if (configured) {
        const parsed = Number(configured);
        if (Number.isFinite(parsed)) {
            return parsed;
        }
    }
    return process.env.NODE_ENV === 'development' ? 1.0 : 0.1;
}

const sentryEnvironment = process.env.REACT_APP_ENVIRONMENT || process.env.NODE_ENV || 'development';
const dsn = (process.env.REACT_APP_SENTRY_DSN ?? '').trim();
// Staging uses the same Sentry project as production; do not send events (no alert noise).
const sentryReportingEnabled = sentryEnvironment.toLowerCase() === 'production';
if (dsn && sentryReportingEnabled) {
    Sentry.init({
        dsn,
        environment: sentryEnvironment,
        release: process.env.REACT_APP_VERSION,
        sendDefaultPii: false,
        integrations: [
            reactRouterV6BrowserTracingIntegration({
                useEffect,
                useLocation,
                useNavigationType,
                createRoutesFromChildren,
                matchRoutes
            }),
            Sentry.replayIntegration({
                maskAllText: true,
                blockAllMedia: true
            })
        ],
        tracesSampleRate: resolveTracesSampleRate(),
        tracePropagationTargets: buildTracePropagationTargets(),
        replaysSessionSampleRate: 0.1,
        replaysOnErrorSampleRate: 1.0
    });
}
